import { TaskRecorder } from "./TaskRecorder";

/**
 * Options for waitForElement task
 */
export interface WaitForElementOptions {
  timeout?: number;
}

/**
 * Options for scroll task
 */
export interface ScrollOptions {
  x?: number;
  y?: number;
  block?: ScrollLogicalPosition;
}

/**
 * Options for type task
 */
export interface TypeOptions {
  typeDelay?: number;
  /* When true, use the Enter key to submit the form after typing. Default `false` */
  submit?: boolean;
}

/**
 * Options for click task
 */
export interface ClickOptions {
  delay?: number;
}

/**
 * Options for wait task
 */
export interface WaitOptions {
  duration: number;
}

/**
 * Options for waitForNavigation task
 */
export interface WaitForNavigationOptions {
  timeout?: number;
}

/**
 * Proxy class that provides fluent API methods for browser automation.
 * Each method records a task and returns this for chaining.
 */
export class TabProxy {
  constructor(private recorder: TaskRecorder) {}

  /**
   * Wait for an element to appear on the page
   */
  async waitForElement(
    selector: string,
    options?: WaitForElementOptions
  ): Promise<this> {
    this.recorder.add({
      type: "waitForElement",
      selector,
      timeout: options?.timeout,
    });
    return this;
  }

  /**
   * Type text into an input element
   */
  async type(
    selector: string,
    text: string,
    options?: TypeOptions
  ): Promise<this> {
    this.recorder.add({
      type: "type",
      selector,
      text,
      typeDelay: options?.typeDelay,
      submit: options?.submit,
    });
    return this;
  }

  /**
   * Click on an element
   */
  async click(selector: string, options?: ClickOptions): Promise<this> {
    this.recorder.add({
      type: "click",
      selector,
      delay: options?.delay,
    });
    return this;
  }

  /**
   * Navigate to a URL
   * Automatically adds a waitForNavigation task after the navigation
   */
  async navigate(url: string): Promise<this> {
    this.recorder.add({
      type: "navigate",
      url,
    });

    // Automatically add waitForNavigation after navigate
    this.recorder.add({
      type: "waitForNavigation",
      timeout: 10000,
    });

    return this;
  }

  /**
   * Wait for a specific duration
   */
  async wait(options: WaitOptions): Promise<this> {
    this.recorder.add({
      type: "wait",
      duration: options.duration,
    });
    return this;
  }

  /**
   * Wait for navigation to complete
   */
  async waitForNavigation(options?: WaitForNavigationOptions): Promise<this> {
    this.recorder.add({
      type: "waitForNavigation",
      timeout: options?.timeout,
    });
    return this;
  }

  /**
   * Get text content from an element
   */
  async getText(selector: string): Promise<this> {
    this.recorder.add({
      type: "getText",
      selector,
    });
    return this;
  }

  /**
   * Get text content from all matching elements
   */
  async getAllText(selector: string): Promise<this> {
    this.recorder.add({
      type: "getAllText",
      selector,
    });
    return this;
  }

  /**
   * Submit a form
   */
  async submit(selector: string): Promise<this> {
    this.recorder.add({
      type: "submit",
      selector,
    });
    return this;
  }

  /**
   * Select an option from a dropdown
   */
  async select(selector: string, value: string): Promise<this> {
    this.recorder.add({
      type: "select",
      selector,
      value,
    });
    return this;
  }

  /**
   * Set checkbox state
   */
  async checkbox(selector: string, checked: boolean): Promise<this> {
    this.recorder.add({
      type: "checkbox",
      selector,
      checked,
    });
    return this;
  }

  /**
   * Scroll to an element or by coordinates
   */
  async scroll(selector?: string, options?: ScrollOptions): Promise<this> {
    this.recorder.add({
      type: "scroll",
      selector,
      x: options?.x,
      y: options?.y,
      block: options?.block,
    });
    return this;
  }

  /**
   * Click on an element within a container that contains specific text
   */
  async clickContaining(
    selector: string,
    containingText: string,
    containerSelector: string,
    options?: ClickOptions
  ): Promise<this> {
    this.recorder.add({
      type: "clickContaining",
      selector,
      containingText,
      containerSelector,
      delay: options?.delay,
    });
    return this;
  }

  /**
   * Click on an element of a specific type whose text contains the given value
   * @param text - The text to search for in the element's content
   * @param elementType - The HTML tag name to search for (e.g., "a", "button", "div")
   * @param options - Optional configuration for exact matching and delay
   */
  async clickElementByText(
    text: string,
    elementType: string,
    options?: { exact?: boolean; delay?: number }
  ): Promise<this> {
    this.recorder.add({
      type: "clickElementByText",
      text,
      elementType,
      exact: options?.exact,
      delay: options?.delay,
    });
    return this;
  }

  /**
   * Press a key on the keyboard
   */
  async pressKey(key: string, targetSelector?: string): Promise<this> {
    this.recorder.add({
      type: "pressKey",
      key,
      targetSelector,
    });
    return this;
  }

  /**
   * Conditionally type text based on matching text content from an element.
   * Reads text from readSelector, finds the first matching condition,
   * and types the corresponding text into the typeSelector.
   */
  async conditionalTypeText(
    readSelector: string,
    conditions: Array<{
      matchText: string;
      typeSelector: string;
      typeText: string;
    }>,
    options?: TypeOptions
  ): Promise<this> {
    this.recorder.add({
      type: "conditionalTypeText",
      readSelector,
      conditions,
      typeDelay: options?.typeDelay,
      submit: options?.submit,
    });
    return this;
  }
}
